/* ═════════════════════════ Onboarding ═══════════════════════════════
   Shown after Google sign-in when the user isn't in any workspace yet.

   Multi-workspace SaaS: every user picks between two paths:
     • "Create a workspace"  → they become admin of a brand-new team.
     • "Join a workspace"    → they enter their admin's email address;
                               a request is sent to that admin.

   If they've already submitted a join-request, we jump straight to the
   waiting-for-approval view (with a "Check status" button that re-polls).

   Visual language matches Login.jsx — Inter, ink on soft grey, #000000 CTAs.
   ═══════════════════════════════════════════════════════════════════ */
import { useState, useEffect } from "react";

const INK = "#000000";
const MUTED = "#6B7280";
const BORDER = "#E4E4E7";
const SOFT = "#F4F4F5";

export default function Onboarding({ pending, request, onChange, onLogout, welcomeFromCheckout = false }) {
  // "create" and "join" are both always available. We default to the tab the
  // user is most likely to want: if they've typed an admin email before and
  // it came back as not-found, they land back on "join" (since their request
  // is null + we kept them pending). Otherwise we default to "join" — most
  // new accounts are teammates joining an existing roaster, not new shops.
  // Visitors arriving from a /pricing checkout always want a fresh
  // workspace, so we land them on "create" instead.
  const [tab, setTab] = useState(welcomeFromCheckout ? "create" : "join");

  // Already waiting on an admin? Skip the picker.
  const waiting = request?.status === "pending";

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 24, background: SOFT, fontFamily: "Inter, sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <img src="/brand/elion-roaster.svg" alt="Elion Roaster" style={{ height: 30, width: "auto", display: "inline-block" }} />
        </div>

        {waiting
          ? <WaitingCard pending={pending} request={request} onChange={onChange} />
          : (
            <Shell>
              <Heading title="Set up your workspace" />
              <p style={copy}>
                Are you setting up Elion Roaster for your roastery, or joining a workspace your team has already created?
              </p>

              <Tabs tab={tab} setTab={setTab} />

              {tab === "create"
                ? <CreateTeamInner pending={pending} onChange={onChange} />
                : <JoinTeamInner pending={pending} onChange={onChange} onSwitchToCreate={() => setTab("create")} />}
            </Shell>
          )}

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>
            Signed in as <strong style={{ color: MUTED }}>{pending?.email}</strong> · {" "}
            <button
              onClick={onLogout}
              style={{ background: "none", border: "none", color: MUTED, textDecoration: "underline", fontSize: 12, cursor: "pointer", padding: 0 }}
            >Use a different account</button>
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Tab switcher ─── */
function Tabs({ tab, setTab }) {
  const base = {
    flex: 1, padding: "10px 12px", fontSize: 14, fontWeight: 600,
    cursor: "pointer", border: `1px solid ${BORDER}`,
    fontFamily: "Inter, sans-serif", background: "#fff", color: MUTED,
  };
  const active = { background: INK, color: "#fff", borderColor: INK };
  return (
    <div style={{ display: "flex", gap: 0, marginBottom: 18 }}>
      <button
        onClick={() => setTab("join")}
        style={{ ...base, ...(tab === "join" ? active : {}), borderRadius: "10px 0 0 10px", borderRight: tab === "join" ? `1px solid ${INK}` : `1px solid ${BORDER}` }}
      >Join a workspace</button>
      <button
        onClick={() => setTab("create")}
        style={{ ...base, ...(tab === "create" ? active : {}), borderRadius: "0 10px 10px 0", borderLeft: "none" }}
      >Create a workspace</button>
    </div>
  );
}

/* ─── Create a new workspace (→ admin of that workspace) ─── */
function CreateTeamInner({ pending, onChange }) {
  const [teamName, setTeamName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function create() {
    setBusy(true); setErr("");
    try {
      const r = await fetch("/api/onboarding/create-team", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ teamName }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Couldn't create the workspace");
      // Flag the team creator so the Welcome / demo-picker modal fires for
      // them once and only once. Joiners never see it.
      try { localStorage.setItem("pr:just-created-team", "1"); } catch { /* ignore */ }
      await onChange();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <>
      <p style={{ ...copy, marginTop: -4 }}>
        You'll be set up as admin of the new workspace. Invite your teammates from Settings once you're in.
      </p>

      <label style={labelStyle}>Workspace name</label>
      <input
        type="text"
        value={teamName}
        onChange={e => setTeamName(e.target.value)}
        placeholder="e.g. Acme Coffee Roasters"
        style={inputStyle}
      />

      {err && <Err msg={err} />}

      <button onClick={create} disabled={busy || !teamName.trim()} style={ctaStyle(busy || !teamName.trim())}>
        {busy ? "Setting up..." : "Create my workspace"}
      </button>

      <p style={footStyle}>
        You'll be this workspace's admin with full access. Teammates you invite get their own Google sign-in.
      </p>
    </>
  );
}

/* ─── Join an existing workspace (email the admin for approval) ─── */
function JoinTeamInner({ pending, onChange, onSwitchToCreate }) {
  const [name, setName] = useState(pending?.name || "");
  const [adminEmail, setAdminEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function join() {
    setBusy(true); setErr("");
    try {
      const r = await fetch("/api/onboarding/join-team", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ name, adminEmail }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || "Couldn't submit your request");
      await onChange();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <>
      <p style={{ ...copy, marginTop: -4 }}>
        Your admin will get an email and an in-app notification. Once they approve, you're in.
      </p>

      <label style={labelStyle}>Your name</label>
      <input
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Your name"
        style={inputStyle}
      />

      <label style={{ ...labelStyle, marginTop: 14 }}>Admin's email</label>
      <input
        type="email"
        value={adminEmail}
        onChange={e => setAdminEmail(e.target.value)}
        placeholder="admin@yourcompany.com"
        style={inputStyle}
      />
      <p style={{ ...footStyle, marginTop: 6 }}>
        Use the email your admin uses to sign in to Elion Roaster.
      </p>

      {err && <Err msg={err} />}

      <button onClick={join} disabled={busy || !adminEmail || !name} style={ctaStyle(busy || !adminEmail || !name)}>
        {busy ? "Sending..." : "Request access"}
      </button>

      <p style={footStyle}>
        Don't see your admin here? <button onClick={onSwitchToCreate} style={linkStyle}>Create a new workspace instead</button>.
      </p>
    </>
  );
}

/* ─── Waiting-for-approval state ─── */
function WaitingCard({ pending, request, onChange }) {
  const [checking, setChecking] = useState(false);

  // Gentle auto-refresh every 20s so an approve on the admin's side shows up
  // without the requester having to refresh the page manually.
  useEffect(() => {
    const t = setInterval(onChange, 20000);
    return () => clearInterval(t);
  }, [onChange]);

  async function check() {
    setChecking(true);
    try { await onChange(); }
    finally { setChecking(false); }
  }

  const createdAgo = timeAgo(request.createdAt);
  return (
    <Shell>
      <Heading title="Waiting for approval" />
      <p style={copy}>
        We sent your request to <strong style={{ color: INK }}>{request.targetAdminEmail}</strong>. Once they approve, you'll land in the workspace automatically — no need to sign in again.
      </p>

      <div style={{
        margin: "12px 0 8px 0", padding: "14px 16px",
        background: SOFT, borderRadius: 10, border: `1px solid ${BORDER}`,
      }}>
        <div style={{ fontSize: 13, color: MUTED, marginBottom: 2 }}>Requested as</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>{request.name || pending?.email}</div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 10, marginBottom: 2 }}>Email</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: INK }}>{request.email}</div>
        <div style={{ fontSize: 13, color: MUTED, marginTop: 10, marginBottom: 2 }}>Requested</div>
        <div style={{ fontSize: 15, color: INK }}>{createdAgo}</div>
      </div>

      <button onClick={check} disabled={checking} style={ctaStyle(checking)}>
        {checking ? "Checking..." : "Check status"}
      </button>

      <p style={footStyle}>Checking automatically every 20 seconds.</p>
    </Shell>
  );
}

/* ─── Shared primitives ─── */
function Shell({ children }) {
  return (
    <div style={{
      width: "100%", background: "#fff", borderRadius: 14,
      border: `1px solid ${BORDER}`, padding: 32,
      boxShadow: "0 8px 24px rgba(17,24,39,0.04)",
    }}>{children}</div>
  );
}
function Heading({ title }) {
  return <h1 style={{ fontSize: 22, fontWeight: 600, color: INK, margin: "0 0 6px" }}>{title}</h1>;
}
function Err({ msg }) {
  return (
    <div style={{
      marginTop: 12, padding: "10px 14px", borderRadius: 8,
      background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
      fontSize: 13, lineHeight: 1.5,
    }}>{msg}</div>
  );
}
const copy = { fontSize: 14, color: MUTED, margin: "0 0 20px", lineHeight: 1.5 };
const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6, fontFamily: "Inter, sans-serif" };
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "10px 14px",
  border: `1px solid ${BORDER}`, borderRadius: 10, fontSize: 15,
  fontFamily: "Inter, sans-serif", color: INK, outline: "none",
};
const footStyle = { fontSize: 12, color: "#9CA3AF", lineHeight: 1.55, margin: "12px 0 0" };
const linkStyle = {
  background: "none", border: "none", padding: 0, color: INK,
  textDecoration: "underline", fontSize: 12, cursor: "pointer",
  fontFamily: "Inter, sans-serif",
};
function ctaStyle(disabled) {
  return {
    width: "100%", marginTop: 18, padding: "12px 16px", borderRadius: 10,
    border: `1px solid ${disabled ? "#9CA3AF" : INK}`,
    background: disabled ? "#9CA3AF" : INK, color: "#fff",
    fontSize: 15, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "Inter, sans-serif",
  };
}
function timeAgo(ts) {
  const diffS = Math.floor((Date.now() - ts) / 1000);
  if (diffS < 60) return "just now";
  if (diffS < 3600) return `${Math.floor(diffS / 60)} min ago`;
  if (diffS < 86400) return `${Math.floor(diffS / 3600)}h ago`;
  return `${Math.floor(diffS / 86400)}d ago`;
}
